import h from '../_utils/hyperscript.js';
import scrollTo from '../_utils/scrollTo.js';
export const blockResubmit = (formId, options = {}) => {
  const form = typeof formId === 'string' ? document.getElementById(formId) : formId;
  if (!form) {
    console.warn('block resubmit, element not found with ID: ', formId);
    return;
  }
  options = Object.assign({
    reportValidity: false,
    textUnknownField: 'Unknown field',
    textFeedbackIntro: 'Some fields have been filled in incorrectly. Please check the list below to see which ones and what needs to be fixed.',
    textPlaceholder: 'A correct input is for example "{%placeholder%}".',
    textValueMissing: '{%field%} is required, but not filled in.',
    textTypeMismatch: '{%field%} must be of a valid type.',
    textPatternMismatch: '{%field%} does not match the expected format.',
    textRangeUnderflow: '{%field%} is too low.',
    textRangeOverflow: '{%field%} is too high.',
    textTooShort: '{%field%} is too short.',
    textTooLong: '{%field%} is too long.',
    textStepMismatch: '{%field%} does not match the step size.',
    textBadInput: '{%field%} contains invalid input.'
  }, options);
  const getFieldLabel = input => {
    const id = input.id;
    if (id) {
      const label = form.querySelector('label[for="' + id + '"]');
      if (label) {
        return label.textContent.trim();
      }
    }
    const fieldset = input.closest('fieldset');
    if (fieldset) {
      const legend = fieldset.querySelector('legend');
      if (legend) {
        return legend.textContent.trim();
      }
    }
    return input.name || input.placeholder || options.textUnknownField;
  };
  const inputs = Array.from(form.querySelectorAll('input:not([type="hidden"]),textarea,select'));
  // On blur check field validity and mark invalid if not a required field.
  const setupInput = input => {
    input.addEventListener('blur', () => {
      const validity = input.validity;
      const isInvalid = validity.typeMismatch || validity.patternMismatch || validity.rangeOverflow || validity.rangeUnderflow || validity.stepMismatch || validity.tooLong || validity.tooShort || validity.badInput;
      input.setAttribute('aria-invalid', isInvalid ? 'true' : 'false');
    });
  };
  for (const input of inputs) {
    setupInput(input);
  }

  // Store state.
  let statusAlert = null;
  let canSubmit = true;
  let submitIndex = 0;
  const reportValidity = () => {
    // Remove previous alert on resubmission.
    if (statusAlert) {
      statusAlert.remove();
      statusAlert = null;
    }
    const errors = [];
    for (const input of inputs) {
      let error = '';
      if (input.required && input.validity.valueMissing && !input.value.trim()) {
        error = options.textValueMissing;
      } else if (input.validity.typeMismatch) {
        error = options.textTypeMismatch;
        if (input.getAttribute('placeholder')) {
          error += ' ' + options.textPlaceholder;
        }
      } else if (input.validity.patternMismatch) {
        error = options.textPatternMismatch;
        if (input.getAttribute('placeholder')) {
          error += ' ' + options.textPlaceholder;
        }
      } else if (input.validity.rangeUnderflow) {
        error = options.textRangeUnderflow;
      } else if (input.validity.rangeOverflow) {
        error = options.textRangeOverflow;
      } else if (input.validity.tooLong) {
        error = options.textTooLong;
      } else if (input.validity.tooShort) {
        error = options.textTooShort;
      } else if (input.validity.stepMismatch) {
        error = options.textStepMismatch;
      } else if (input.validity.badInput) {
        error = options.textBadInput;
        if (input.getAttribute('placeholder')) {
          error += ' ' + options.textPlaceholder;
        }
      }
      if (error) {
        error = error.replace('{%field%}', getFieldLabel(input));
        error = error.replace('{%placeholder%}', input.getAttribute('placeholder'));
        error = error.replace('{%max_length%}', input.getAttribute('maxlength'));
        error = error.replace('{%min_length%}', input.getAttribute('minlength'));
        error = error.replace('{%max_value%}', input.getAttribute('max'));
        error = error.replace('{%min_value%}', input.getAttribute('min'));
        error = error.replace('{%step_size%}', input.getAttribute('step'));
        if (errors.indexOf(error) < 0) {
          errors.push(error);
        }
        input.setAttribute('aria-invalid', 'true');
      } else {
        input.setAttribute('aria-invalid', 'false');
      }
    }
    if (errors.length > 0) {
      statusAlert = h('div', {
        'aria-live': 'polite',
        class: 'alert alert-danger',
        role: 'status'
      }, [h('p', {
        class: 'mb-2'
      }, options.textFeedbackIntro), h('ul', {
        class: 'mb-0'
      }, errors.map(message => h('li', {}, message)))]);
      form.insertAdjacentElement('afterbegin', statusAlert);
      scrollTo(statusAlert);
    }
    return errors.length === 0;
  };
  const handleValidation = () => {
    if (options.reportValidity) {
      reportValidity();
    }
  };
  const buttonSelector = 'button:not([type]),button[type="submit"],input[type="submit"]';
  const buttons = [];
  const addButtons = () => {
    const newButtons = Array.from(form.querySelectorAll(buttonSelector)).filter(button => !buttons.includes(button)
    // Ensure submit buttons are for this form.
    && button.closest('form') === form);
    if (newButtons.length > 0) {
      buttons.push(...newButtons);
      for (const button of newButtons) {
        button.addEventListener('click', handleValidation);
      }
    }
  };
  const updateButtons = () => {
    // Check for new buttons and add them to the submission list whilst your at it.
    addButtons();
    for (const button of buttons) {
      if (canSubmit) {
        button.classList.add('disabled');
        button.setAttribute('disabled', 'true');
      } else {
        button.classList.remove('disabled');
        button.removeAttribute('disabled');
      }
    }
  };
  const releaseButtons = () => {
    canSubmit = true;
    updateButtons();
  };
  form.addEventListener('invalid', releaseButtons);
  const handleSubmission = event => {
    canSubmit = true;
    if (options.reportValidity) {
      canSubmit = reportValidity();
    }
    canSubmit = canSubmit && form.checkValidity();
    if (canSubmit) {
      // Automatically make buttons available in case request takes to long or something went wrong.
      submitIndex++;
      let submitTemp = submitIndex;
      setTimeout(() => {
        if (submitIndex === submitTemp) {
          releaseButtons();
        }
      }, 15e3);
    } else {
      form.reportValidity();
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    updateButtons();
  };
  form.addEventListener('submit', handleSubmission);
  addButtons();
};
export default blockResubmit;